import { FunctionComponent, ReactNode, useEffect, useRef } from "react";

import { useCoralDrawerContext } from "@voxmedia/duet-context/context/coral-drawer-context";
import { CoralPreviewOverrideProvider } from "src/context/CoralPreviewOverrideContext";
import { useCommentsPreviewMode } from "src/hooks/use-comments-preview-mode";
import {
  CORAL_PREVIEW_STORY_ID,
  CORAL_PREVIEW_STORY_URL,
  COMMENTS_PREVIEW_QUERY_VALUE,
} from "src/utils/comments-preview";

import * as styles from "./CommentsPreviewAppWrapper.css";

const CommentsPreviewAutoOpen: FunctionComponent = () => {
  const { openDrawer, prerenderDrawer } = useCoralDrawerContext();
  const hasOpenedRef = useRef(false);

  useEffect(() => {
    if (hasOpenedRef.current) {
      return;
    }
    hasOpenedRef.current = true;
    prerenderDrawer();
    openDrawer({ disableTransition: true });
  }, [openDrawer, prerenderDrawer]);

  return null;
};

const CommentsPreviewBanner: FunctionComponent = () => (
  <div className={styles.banner} role="status">
    Coral preview: staging thread pinned via{" "}
    <code>?comments={COMMENTS_PREVIEW_QUERY_VALUE}</code>
    {!CORAL_PREVIEW_STORY_URL ? (
      <>
        . Set <code>CORAL_PREVIEW_STORY_URL</code> in{" "}
        <code>comments-preview.ts</code> (staging article permalink) if the
        drawer shows a network error.
      </>
    ) : null}
  </div>
);

/**
 * Development-only wrapper: pins Coral to a staging story ID and auto-opens the
 * comments drawer when `?comments=test` is in the URL.
 */
const CommentsPreviewAppWrapper: FunctionComponent<{ children: ReactNode }> = ({
  children,
}) => {
  const previewMode = useCommentsPreviewMode();

  if (!previewMode) {
    return <>{children}</>;
  }

  return (
    <CoralPreviewOverrideProvider
      override={{
        storyId: CORAL_PREVIEW_STORY_ID,
        ...(CORAL_PREVIEW_STORY_URL
          ? { storyUrl: CORAL_PREVIEW_STORY_URL }
          : {}),
      }}
    >
      <CommentsPreviewAutoOpen />
      <CommentsPreviewBanner />
      {children}
    </CoralPreviewOverrideProvider>
  );
};

export default CommentsPreviewAppWrapper;
